import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import {
  AllowListAuthConfig,
  AllowedPageConfig,
  AllowedSectionConfig,
} from './allow-list-auth.config';
import { MenuService, isMenuIdAllowed } from '../../services/menu.service';
import { ProgramService } from '../../services/program.service';

/**
 * AllowListAuthStore — singleton service that holds the current role's
 * allow-list authorization configuration.
 *
 * **Default behaviour: DENY.**
 * Every query method returns `false` or `[]` when:
 *   - no config is loaded, or
 *   - the requested item is not in the allow-list.
 *
 * Usage:
 *   1. Call `loadConfig(roleSid, programCode)` once after program+role selection.
 *      Subscribe to the returned Observable to know when the config is ready.
 *   2. Use query methods anywhere in the app.
 *   3. Call `clearConfig()` on role switch / logout.
 */
@Injectable({ providedIn: 'root' })
export class AllowListAuthStore {

  private config: AllowListAuthConfig | null = null;
  private allowedRoutes = new Set<string>();

  /**
   * Monotonic counter incremented on every loadConfig / clearConfig call.
   * When an in-flight API response arrives, its captured version is compared
   * against this value — if they differ, the response is stale and discarded.
   */
  private loadVersion = 0;

  /** Emits `true` once config has been loaded. Resets to `false` on clear or during reload. */
  readonly loaded$ = new BehaviorSubject<boolean>(false);

  constructor(
    private menuService: MenuService,
    private programService: ProgramService
  ) {}

  // -----------------------------------------------------------------------
  // Lifecycle
  // -----------------------------------------------------------------------

  /**
   * Load the allow-list config for the given role from the API.
   * Derives allowed routes from main_menu.json + allowedMenuIds.
   *
   * Safe against rapid calls — only the most recent request's response is applied.
   * Previous config is cleared immediately so directives deny during the API call.
   *
   * @returns Observable that emits the loaded config, or null if not found / error / stale.
   */
  loadConfig(roleSid: number, programCode: string): Observable<AllowListAuthConfig | null> {
    const thisVersion = ++this.loadVersion;

    // Invalidate immediately so directives deny while the API call is in-flight
    this.config = null;
    this.allowedRoutes.clear();
    this.loaded$.next(false);

    return this.programService.fetchRoleAuthorization(programCode, roleSid).pipe(
      map(response => {
        // A newer loadConfig / clearConfig was called — this response is stale
        if (thisVersion !== this.loadVersion) return this.config;

        const raw = response?.responsedata;
        const authData = raw?.page_authorization;

        // Validate the minimum expected shape before trusting the payload
        if (
          authData &&
          Array.isArray(authData.menus?.allowedMenuIds) &&
          typeof authData.pages === 'object' && authData.pages !== null
        ) {
          this.config = {
            roleSid: raw.role_sid,
            roleName: raw.role_name,
            menus: authData.menus,
            pages: authData.pages as Record<string, AllowedPageConfig>,
          };
        } else {
          this.config = null;
        }

        this.allowedRoutes.clear();

        if (this.config) {
          this.deriveAllowedRoutes(programCode);
        }

        this.loaded$.next(this.config !== null);
        return this.config;
      }),
      catchError(() => {
        // Stale — a newer call already owns the state
        if (thisVersion !== this.loadVersion) return of(null);

        this.config = null;
        this.allowedRoutes.clear();
        this.loaded$.next(false);
        return of(null);
      })
    );
  }

  /** Clear config (e.g. on role switch or logout). Also cancels any in-flight loadConfig. */
  clearConfig(): void {
    this.loadVersion++;
    this.config = null;
    this.allowedRoutes.clear();
    this.loaded$.next(false);
  }

  /** Whether a config is currently loaded. */
  isLoaded(): boolean {
    return this.config !== null;
  }

  /** Get a deep copy of the raw config (safe for debugging — cannot mutate internal state). */
  getConfig(): AllowListAuthConfig | null {
    if (!this.config) return null;
    return structuredClone(this.config);
  }

  /**
   * Get allowed menu IDs for the current role.
   * Admin config uses granular IDs (e.g. consumerRegistrationGrid) for page-level control,
   * but main_menu.json uses a single ID (consumerRegistration). This method maps
   * granular admin IDs to their display equivalents so menu filtering works.
   */
  getAllowedMenuIds(): string[] {
    const raw = this.config?.menus.allowedMenuIds ?? [];
    const ids = new Set(raw);
    for (const id of raw) {
      const displayId = AllowListAuthStore.ADMIN_TO_MENU_MAP[id];
      if (displayId) ids.add(displayId);
    }
    return Array.from(ids);
  }

  // -----------------------------------------------------------------------
  // Route checks
  // -----------------------------------------------------------------------

  /**
   * Check if a route path (landing_component) is allowed for the current role.
   * Returns `false` if no config is loaded or route is not in the allowed set.
   *
   * Matching strategy: splits the URL into path segments and checks if any
   * segment exists in the allowed routes set. This works because Angular route
   * segments are unique component names (e.g. 'referralList', 'newReferral').
   * If two unrelated features ever share a segment name, this would need to
   * switch to full-path matching.
   */
  isRouteAllowed(routePath: string): boolean {
    if (!this.config || !routePath) return false;
    const segments = routePath.split('/').filter(Boolean);
    return segments.some(segment => this.allowedRoutes.has(segment));
  }

  // -----------------------------------------------------------------------
  // Menu checks
  // -----------------------------------------------------------------------

  /** Check if a menu item is allowed for the current role. */
  isMenuAllowed(menuId: string): boolean {
    if (!this.config) return false;
    return this.config.menus.allowedMenuIds.includes(menuId);
  }

  // -----------------------------------------------------------------------
  // Page-level checks (list pages)
  // -----------------------------------------------------------------------

  /** Check if an action is allowed on a given page. */
  isActionAllowed(pageId: string, actionId: string): boolean {
    const page = this.getPage(pageId);
    if (!page || !page.allowedActions) return false;
    return page.allowedActions.includes(actionId);
  }

  /** Check if a button is allowed on a given page. */
  isButtonAllowed(pageId: string, buttonId: string): boolean {
    const page = this.getPage(pageId);
    if (!page) return false; // no page config — deny
    if (!page.allowedButtons) return false;
    return page.allowedButtons.includes(buttonId);
  }

  /** Check if a column is allowed on a given page. */
  isColumnAllowed(pageId: string, columnId: string): boolean {
    const page = this.getPage(pageId);
    if (!page || !page.allowedColumns) return false;
    return page.allowedColumns.includes(columnId);
  }

  /** Get all allowed actions for a page. Returns `[]` if denied or not found. */
  getAllowedActions(pageId: string): string[] {
    return this.getPage(pageId)?.allowedActions ?? [];
  }

  /** Get all allowed buttons for a page. Returns `[]` if denied or not found. */
  getAllowedButtons(pageId: string): string[] {
    return this.getPage(pageId)?.allowedButtons ?? [];
  }

  /** Get all allowed columns for a page. Returns `[]` if denied or not found. */
  getAllowedColumns(pageId: string): string[] {
    return this.getPage(pageId)?.allowedColumns ?? [];
  }

  // -----------------------------------------------------------------------
  // Tab checks (detail pages)
  // -----------------------------------------------------------------------

  /** Check if a tab is allowed on a detail page. */
  isTabAllowed(pageId: string, tabId: string): boolean {
    const page = this.getPage(pageId);
    if (!page) return false; // no page config — deny
    if (!page.allowedTabs) return true; // LIST pages have no tabs — no restrictions
    return page.allowedTabs.some(at => at.toLowerCase() === tabId.toLowerCase());
  }

  /** Get all allowed tabs for a page. Returns `[]` if denied or not found. */
  getAllowedTabs(pageId: string): string[] {
    return this.getPage(pageId)?.allowedTabs ?? [];
  }

  /**
   * Check if a tab has edit access.
   * For tabs with configured sections, returns true if any section has canEdit.
   * For tabs without sections (e.g. consumerRates), uses page-level buttons/actions
   * as a proxy — view preset disables all, full preset enables them.
   */
  isTabEditable(pageId: string, tabId: string): boolean {
    const page = this.getPage(pageId);
    if (!page) return false;
    if (!this.isTabAllowed(pageId, tabId)) return false;

    if (page.tabs) {
      const tab = page.tabs.find(t => t.id.toLowerCase() === tabId.toLowerCase());
      if (tab && tab.sections.length > 0) {
        return tab.sections.some(s => s.canEdit);
      }
    }

    // No sections configured for this tab — fall back to page-level indicators
    return (page.allowedButtons?.length ?? 0) > 0 || (page.allowedActions?.length ?? 0) > 0;
  }

  // -----------------------------------------------------------------------
  // Section checks (detail pages)
  // -----------------------------------------------------------------------

  /** Can the current role VIEW this section? */
  isSectionVisible(pageId: string, tabId: string, sectionId: string): boolean {
    const page = this.getPage(pageId);
    if (!page) return false; // no page config — deny
    const section = this.getSection(pageId, tabId, sectionId);
    if (section !== undefined) return section.canView;
    // LIST pages (no allowedTabs) — allow view if page exists
    if (!page.allowedTabs) return true;
    // DETAIL pages — allow view only if tab is allowed (graceful for unconfigured sections)
    return this.isTabAllowed(pageId, tabId);
  }

  /** Can the current role EDIT this section? */
  isSectionEditable(pageId: string, tabId: string, sectionId: string): boolean {
    const page = this.getPage(pageId);
    if (!page) return false; // no page config — deny editing
    const section = this.getSection(pageId, tabId, sectionId);
    if (section !== undefined) return section.canEdit;
    // LIST pages (no allowedTabs) — allow edit if page exists
    if (!page.allowedTabs) return true;
    // DETAIL pages — deny edit for unconfigured sections
    return false;
  }

  /** Can the current role DELETE in this section? */
  isSectionDeletable(pageId: string, tabId: string, sectionId: string): boolean {
    const page = this.getPage(pageId);
    if (!page) return false; // no page config — deny deletion
    const section = this.getSection(pageId, tabId, sectionId);
    if (section !== undefined) return section.canDelete;
    // LIST pages (no allowedTabs) — allow delete if page exists
    if (!page.allowedTabs) return true;
    // DETAIL pages — deny delete for unconfigured sections
    return false;
  }

  // -----------------------------------------------------------------------
  // Internals
  // -----------------------------------------------------------------------

  private getPage(pageId: string): AllowedPageConfig | undefined {
    return this.config?.pages[pageId];
  }

  private getSection(
    pageId: string,
    tabId: string,
    sectionId: string
  ): AllowedSectionConfig | undefined {
    const page = this.getPage(pageId);
    if (!page || !page.tabs) return undefined;

    const tab = page.tabs.find((t) => t.id.toLowerCase() === tabId.toLowerCase());
    if (!tab) return undefined;

    return tab.sections.find(
      (s) => s.id.toLowerCase() === sectionId.toLowerCase()
    );
  }

  /**
   * Menu IDs whose routes don't appear in any program's main_menu.json.
   * When a menu ID from allowedMenuIds isn't found in main_menu.json,
   * this map provides the route path(s) to allow.
   */
  /** Maps granular admin menu IDs to the actual menu_id in main_menu.json for display. */
  private static readonly ADMIN_TO_MENU_MAP: Record<string, string> = {
    consumerRegistrationGrid: 'consumerRegistration',
    consumerRegistrationMemberView: 'consumerRegistration',
  };

  private static readonly MENU_ROUTE_FALLBACKS: Record<string, string[]> = {
    payrollProcessing:      ['payrollProcessing'],
    fileTriage:             ['fileTriage'],
    payrollNATPay:          ['payrollNATPay'],
    fileUploadNotification: ['fileUploadNotification'],
    authUtilizationAnalysis:['authUtilizationAnalysis'],
    billingAdjustment:      ['billingAdjustment'],
    billingApprovals:       ['billingApprovals'],
    viewBatches:            ['claimBatchesList'],
    viewClaims:             ['viewClaimsList'],
    viewFiles:              ['claimFilesList'],
    externalMembers:        ['enrollmentDetails'],
    consumerRegistrationGrid: ['participantEnrollmentList'],
    consumerRegistrationMemberView: ['participantEnrollmentList'],
  };

  /**
   * Action routes that are only reachable by navigating from within a feature
   * (e.g. clicking "Add" on a list page). Keyed by the feature's landing_component.
   * When a feature's landing_component is allowed, its action routes are also allowed.
   */
  private static readonly FEATURE_ACTION_ROUTES: Record<string, string[]> = {
    authorizations:              ['addAuthorizations', 'spendingPlans'],
    // Its Add button and the View link on each authorization leave the plan list.
    spendingPlans:               ['addAuthorizations', 'authorizations'],
    participantLeadsList:        ['newReferral', 'referralLogic'],
    referralList:                ['newReferral', 'referralLogic'],
    participantEnrollmentList:   ['newReferral', 'referralLogic'],
    providerEnrollmentList:      ['newReferral', 'referralLogic'],
    globalConsumerSearch:        ['newReferral', 'referralLogic'],
    processCodeList:             ['processCode'],
    serviceCodeList:             ['serviceCode'],
    participantProviderRatesList: ['addParticipantProviderRates'],
    payablePersonalAssistantRates: ['addPayablePersonalAssistantRates'],
    billablePersonalAssistantRates: ['addBillablePersonalAssistantRates'],
    caseManagerList:             ['caseManagerDetails'],
    bulkAdjustments:             ['addBulkAdjustment', 'bulkBatchResultList', 'bulkBatchErrorList'],
    timesheetPayrollProcessing:  ['addTimesheetPayrollBatch', 'timesheetPayrollRunResults', 'timesheetPayrollRunErrors'],
    enrollmentDetails:           ['consumerRates', 'sickTime', 'summary', 'enrollmentInformation', 'toDoList', 'service-credentials', 'authorization', 'employers', 'documents', 'forms', 'checklist', 'associations', 'signature', 'additionalAttributes', 'authorizations', 'timesheets', 'invoices', 'benefits', 'purchaseRequests'],
  };

  /**
   * Derive allowed routes from main_menu.json for the loaded config.
   * Reads menu JSON for the given program, then maps
   * allowedMenuIds → landing_component paths + their related action routes.
   */
  private deriveAllowedRoutes(programCode: string): void {
    if (!this.config) return;

    const allowedMenuIds = new Set(this.config.menus.allowedMenuIds);

    try {
      const matchedMenuIds = new Set<string>();
      const menu = this.menuService.getMenu(programCode);
      for (const category of Object.keys(menu)) {
        const items = menu[category];
        if (!Array.isArray(items)) continue;

        for (const item of items) {
          // Honour MENU_ACCESS_INHERITANCE here too, otherwise a menu item that is visible would
          // still have its route denied by isRouteAllowed.
          if (isMenuIdAllowed(item.menu_id, allowedMenuIds) && item.landing_component) {
            matchedMenuIds.add(item.menu_id);
            this.allowedRoutes.add(item.landing_component);
            const actionRoutes = AllowListAuthStore.FEATURE_ACTION_ROUTES[item.landing_component] ?? [];
            actionRoutes.forEach(r => this.allowedRoutes.add(r));
          }
        }
      }

      // Menu IDs that weren't found in main_menu.json — use fallback route map
      for (const menuId of allowedMenuIds) {
        if (matchedMenuIds.has(menuId)) continue;
        const fallbackRoutes = AllowListAuthStore.MENU_ROUTE_FALLBACKS[menuId];
        if (fallbackRoutes) {
          fallbackRoutes.forEach(r => {
            this.allowedRoutes.add(r);
            const actionRoutes = AllowListAuthStore.FEATURE_ACTION_ROUTES[r] ?? [];
            actionRoutes.forEach(ar => this.allowedRoutes.add(ar));
          });
        }
      }
    } catch (e) {
      console.warn(`[AllowListAuthStore] Failed to derive routes for program "${programCode}". All routes denied.`, e);
    }
  }
}
